import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { AvatarInitials } from '@/components/common/admin/AvatarInitials';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';

export interface PersonCellProps {
  firstName?: string;
  lastName?: string;
  email?: string;
  /**
   * Avatar size variant.
   * - `sm` (default): 28px — used in dense table rows.
   * - `md`: 48px — used in detail headers.
   */
  size?: 'sm' | 'md';
  /**
   * Optional override for the secondary line. When provided, takes precedence
   * over `email` so callers can surface contextual info (e.g. unit number,
   * lease end date) instead of the email address.
   */
  subLine?: string;
  testID?: string;
}

/**
 * Compact person row primitive: gradient initials avatar + (name + sub-line).
 * Used by both resident and admin redesign surfaces (dense table rows when
 * `size='sm'`, detail headers when `size='md'`).
 */
export const PersonCell: React.FC<PersonCellProps> = React.memo(
  ({ firstName, lastName, email, size = 'sm', subLine, testID }) => {
    const styles = useStyles();
    const avatarSize = size === 'sm' ? 28 : 48;
    const name = [firstName, lastName].filter(Boolean).join(' ');
    const sub = subLine ?? email ?? '';
    return (
      <View style={styles.row} testID={testID}>
        <AvatarInitials
          firstName={firstName}
          lastName={lastName}
          size={avatarSize}
        />
        <View style={styles.text}>
          {name ? (
            <Text style={styles.name} numberOfLines={1}>
              {name}
            </Text>
          ) : null}
          {sub ? (
            <Text style={styles.sub} numberOfLines={1}>
              {sub}
            </Text>
          ) : null}
        </View>
      </View>
    );
  },
);

PersonCell.displayName = 'PersonCell';

const useStyles = () =>
  useThemedStyles(({ colors }) =>
    StyleSheet.create({
      row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        flex: 1,
      },
      text: {
        flex: 1,
        minWidth: 0,
      },
      name: {
        fontSize: 14,
        fontWeight: '600',
        color: colors.text.primary,
      },
      sub: {
        fontSize: 11,
        color: colors.text.dim,
        marginTop: 1,
      },
    }),
  );
